/*
 * Client Portfolios tab — drag-and-drop editor for assigning sites to
 * portfolios inside a client. Admins pick a client, then rearrange sites
 * between columns (portfolios + a synthetic "Unassigned" column) via native
 * HTML5 drag events. Each drop dispatches a single `PATCH /sites/{id}` so
 * changes persist immediately; no bulk save button needed.
 *
 * Also lets admins add / rename / delete portfolios in-place via a compact
 * chip-style header on each column so re-organising a client's portfolio
 * doesn't require raw JSON edits.
 */
import { useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { ActionButton, GhostButton, PillBadge, inputClass } from "../Primitives";
import { Loader2, GripVertical, Plus, Pencil, Trash2, Check, X, Building2, Users, ChevronDown } from "lucide-react";

const UNASSIGNED_COLUMN_ID = "__unassigned__";
const uuid = () => (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`);

export function ClientPortfoliosTab() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [client, setClient] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [dragSiteId, setDragSiteId] = useState(null);
  const [hoverColumnId, setHoverColumnId] = useState(null);
  // Column-reorder state — separate from site dragging so the two kinds of
  // drag can coexist without hover-highlight bleed.
  const [dragColumnId, setDragColumnId] = useState(null);
  const [hoverReorderId, setHoverReorderId] = useState(null);

  // Load agency's client list on mount — small (<200) so single fetch is fine.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/clients");
        const list = (r.data || []).slice().sort((a, b) => a.company_name.localeCompare(b.company_name));
        setClients(list);
        // Auto-select the first client for a one-tap start; the admin can
        // pick another from the dropdown.
        if (list.length > 0) setClientId(list[0].id);
      } catch (e) {
        toast.error(errorMessage(e, "Failed to load clients"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When the selected client changes, hydrate that client's freshest doc
  // via GET /clients/{id} (added iter184k) so any portfolios edited from
  // the Clients page since the initial list load are picked up. Sites are
  // pulled from /sites filtered by client_id.
  useEffect(() => {
    if (!clientId) { setClient(null); setSites([]); return; }
    let cancelled = false;
    (async () => {
      setSaving(true);
      try {
        const [cRes, sRes] = await Promise.all([
          api.get(`/clients/${clientId}`),
          api.get("/sites"),
        ]);
        if (cancelled) return;
        setClient(cRes.data);
        // Keep the parent list in sync so switching back doesn't stale.
        setClients((cur) => cur.map((c) => c.id === clientId ? cRes.data : c));
        setSites((sRes.data || []).filter((s) => s.client_id === clientId));
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e, "Failed to load client"));
      } finally {
        if (!cancelled) setSaving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  // `client?.portfolios` returns a fresh empty array literal every render
  // when the client isn't loaded yet — wrap in useMemo so `sitesByColumn`
  // below doesn't rebuild on every keystroke.
  const portfolios = useMemo(() => client?.portfolios || [], [client]);

  // Kanban columns — Unassigned first, then each portfolio in the client's
  // saved order. `sitesByColumn` powers the render + drop targets.
  const sitesByColumn = useMemo(() => {
    const map = { [UNASSIGNED_COLUMN_ID]: [] };
    for (const p of portfolios) map[p.id] = [];
    for (const s of sites) {
      const key = s.portfolio_id && map[s.portfolio_id] !== undefined ? s.portfolio_id : UNASSIGNED_COLUMN_ID;
      map[key].push(s);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return map;
  }, [sites, portfolios]);

  // ────────────────────────────────────────────────────────────
  // Drag & drop — native HTML5 events (no library dependency).
  // We support two independent drag kinds encoded via dataTransfer types:
  //   • text/plain           → site card between columns
  //   • application/x-column → whole portfolio column reordering
  // The Unassigned column is always leftmost and cannot be dragged or
  // become a reorder drop-target.
  // ────────────────────────────────────────────────────────────
  const handleDragStart = (siteId) => (e) => {
    setDragSiteId(siteId);
    try {
      e.dataTransfer.setData("text/plain", siteId);
      e.dataTransfer.effectAllowed = "move";
    } catch { /* setData can throw in Safari private mode — safe to ignore */ }
  };
  const handleDragEnd = () => { setDragSiteId(null); setHoverColumnId(null); setDragColumnId(null); setHoverReorderId(null); };
  const handleDragOver = (columnId) => (e) => {
    // Suppress body-drop when a column is being dragged — sites-only zone.
    if (dragColumnId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (columnId !== hoverColumnId) setHoverColumnId(columnId);
  };
  const handleDragLeave = (columnId) => () => {
    if (hoverColumnId === columnId) setHoverColumnId(null);
  };

  const moveSite = async (site, targetColumnId) => {
    const nextPortfolioId = targetColumnId === UNASSIGNED_COLUMN_ID ? null : targetColumnId;
    if ((site.portfolio_id || null) === nextPortfolioId) return; // no-op
    // Optimistic swap
    const prev = sites;
    setSites((cur) => cur.map((s) => s.id === site.id ? { ...s, portfolio_id: nextPortfolioId } : s));
    try {
      // Merge with the site's existing fields to preserve required ones
      // (address, name, guards_required) that PUT would otherwise blank.
      await api.put(`/sites/${site.id}`, { ...site, portfolio_id: nextPortfolioId });
      toast.success(`Moved ${site.name} → ${nextPortfolioId ? portfolios.find(p => p.id === nextPortfolioId)?.name : "Unassigned"}`);
    } catch (e) {
      // Roll back on failure
      setSites(prev);
      toast.error(errorMessage(e, "Move failed"));
    }
  };

  const handleDrop = (columnId) => async (e) => {
    e.preventDefault();
    // Column-drag drops are handled per-header (see PortfolioColumn) — bail
    // out here so we don't accidentally treat a column drop as a site drop.
    if (dragColumnId) return;
    const siteId = e.dataTransfer.getData("text/plain") || dragSiteId;
    setDragSiteId(null);
    setHoverColumnId(null);
    if (!siteId) return;
    const site = sites.find((s) => s.id === siteId);
    if (!site) return;
    await moveSite(site, columnId);
  };

  // ────────────────────────────────────────────────────────────
  // Column reordering — drag a portfolio column header to another column
  // header to reposition. The result is a fresh portfolios[] whose index
  // ordering matches on-disk state after a PUT /clients/{id}.
  // ────────────────────────────────────────────────────────────
  const handleColumnDragStart = (portfolioId) => (e) => {
    setDragColumnId(portfolioId);
    try {
      e.dataTransfer.setData("application/x-column", portfolioId);
      e.dataTransfer.effectAllowed = "move";
    } catch { /* ignore */ }
  };
  const handleColumnDragOver = (targetPortfolioId) => (e) => {
    if (!dragColumnId || dragColumnId === targetPortfolioId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hoverReorderId !== targetPortfolioId) setHoverReorderId(targetPortfolioId);
  };
  const handleColumnDragLeave = (targetPortfolioId) => () => {
    if (hoverReorderId === targetPortfolioId) setHoverReorderId(null);
  };
  const handleColumnDrop = (targetPortfolioId) => async (e) => {
    // Read the source BEFORE calling preventDefault so we can bail out
    // silently when the drop isn't actually a column drag — otherwise the
    // wrapper's `!e.defaultPrevented` gate would swallow every non-column
    // drop (site drags between columns most notably).
    const sourceId = dragColumnId || e.dataTransfer.getData("application/x-column");
    if (!sourceId || sourceId === targetPortfolioId) return;
    e.preventDefault();
    e.stopPropagation();
    setDragColumnId(null);
    setHoverReorderId(null);
    const srcIdx = portfolios.findIndex((p) => p.id === sourceId);
    const dstIdx = portfolios.findIndex((p) => p.id === targetPortfolioId);
    if (srcIdx < 0 || dstIdx < 0) return;
    const next = [...portfolios];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, moved);
    // Optimistic reorder
    const prev = portfolios;
    setClient((c) => ({ ...c, portfolios: next }));
    setClients((cur) => cur.map((c) => c.id === clientId ? { ...c, portfolios: next } : c));
    try {
      const ok = await persistPortfolios(next);
      if (!ok) throw new Error("persist failed");
      toast.success(`Moved "${moved.name}" to position ${dstIdx + 1}`);
    } catch {
      // Roll back on failure
      setClient((c) => ({ ...c, portfolios: prev }));
      setClients((cur) => cur.map((c) => c.id === clientId ? { ...c, portfolios: prev } : c));
    }
  };

  // ────────────────────────────────────────────────────────────
  // Portfolio CRUD (add / rename / delete) — persists via PATCH on the
  // client's `portfolios[]` field.
  // ────────────────────────────────────────────────────────────
  const persistPortfolios = async (nextPortfolios) => {
    if (!client) return false;
    setSaving(true);
    try {
      // PUT expects the full ClientIn shape — spread the current client to
      // preserve every unrelated field (contact, address, contract, etc.).
      const res = await api.put(`/clients/${clientId}`, { ...client, portfolios: nextPortfolios });
      const updated = res.data || { ...client, portfolios: nextPortfolios };
      setClient(updated);
      setClients((cur) => cur.map((c) => c.id === clientId ? updated : c));
      return true;
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addPortfolio = async () => {
    const name = newPortfolioName.trim();
    if (!name) return;
    if (portfolios.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
      toast.error("A portfolio with that name already exists");
      return;
    }
    const next = [...portfolios, { id: uuid(), name }];
    const ok = await persistPortfolios(next);
    if (ok) { setNewPortfolioName(""); toast.success(`Added "${name}"`); }
  };

  const startRename = (p) => { setRenamingId(p.id); setRenameValue(p.name); };
  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };
  const commitRename = async () => {
    const name = renameValue.trim();
    if (!name) { cancelRename(); return; }
    const next = portfolios.map((p) => p.id === renamingId ? { ...p, name } : p);
    const ok = await persistPortfolios(next);
    if (ok) { cancelRename(); toast.success("Renamed"); }
  };

  const removePortfolio = async (p) => {
    const count = sitesByColumn[p.id]?.length || 0;
    const msg = count > 0
      ? `Delete "${p.name}"? Its ${count} site${count > 1 ? "s" : ""} will move to Unassigned.`
      : `Delete "${p.name}"?`;
    if (!window.confirm(msg)) return;
    const next = portfolios.filter((x) => x.id !== p.id);
    const ok = await persistPortfolios(next);
    if (!ok) return;
    // Any sites still pointing at the deleted portfolio are automatically
    // treated as Unassigned by the render layer, but persist that too so
    // the on-disk state matches the UI.
    const orphans = sites.filter((s) => s.portfolio_id === p.id);
    for (const s of orphans) {
      try { await api.put(`/sites/${s.id}`, { ...s, portfolio_id: null }); } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("orphan reassign failed:", err?.response?.status);
      }
    }
    setSites((cur) => cur.map((s) => s.portfolio_id === p.id ? { ...s, portfolio_id: null } : s));
    toast.success(`Deleted "${p.name}"`);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-zinc-500 flex items-center justify-center gap-2">
        <Loader2 className="size-4 animate-spin" /> Loading clients…
      </div>
    );
  }
  if (clients.length === 0) {
    return (
      <div className="p-8 text-center text-zinc-500" data-testid="client-portfolios-empty">
        No clients yet — add one from the Clients tab first.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="client-portfolios-tab">
      {/* Client picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="label-mute flex items-center gap-1"><Users className="size-3" /> Client</div>
        <div className="relative">
          <select
            data-testid="portfolio-editor-client-select"
            className={`${inputClass} pr-8 min-w-[240px]`}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
          <ChevronDown className="size-4 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {saving && <Loader2 className="size-3 animate-spin text-zinc-500" />}
        <PillBadge data-testid="portfolio-editor-site-count">{sites.length} site{sites.length === 1 ? "" : "s"}</PillBadge>
      </div>

      {/* Add-portfolio row */}
      <div className="flex items-center gap-2">
        <input
          data-testid="portfolio-editor-new-name"
          className={`${inputClass} max-w-xs`}
          placeholder="+ New portfolio name"
          value={newPortfolioName}
          onChange={(e) => setNewPortfolioName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addPortfolio()}
        />
        <ActionButton data-testid="portfolio-editor-add" onClick={addPortfolio} disabled={!newPortfolioName.trim()}>
          <Plus className="size-3.5" /> Add Portfolio
        </ActionButton>
      </div>

      {/* Kanban board */}
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="portfolio-editor-board">
        {/* Unassigned column — always first */}
        <PortfolioColumn
          columnId={UNASSIGNED_COLUMN_ID}
          name="Unassigned"
          isUnassigned
          sites={sitesByColumn[UNASSIGNED_COLUMN_ID]}
          hover={hoverColumnId === UNASSIGNED_COLUMN_ID}
          onDragStartSite={handleDragStart}
          onDragEndSite={handleDragEnd}
          onDragOver={handleDragOver(UNASSIGNED_COLUMN_ID)}
          onDragLeave={handleDragLeave(UNASSIGNED_COLUMN_ID)}
          onDrop={handleDrop(UNASSIGNED_COLUMN_ID)}
        />
        {portfolios.map((p) => {
          const isRenaming = renamingId === p.id;
          return (
            <PortfolioColumn
              key={p.id}
              columnId={p.id}
              name={p.name}
              sites={sitesByColumn[p.id]}
              hover={hoverColumnId === p.id}
              reorderHover={hoverReorderId === p.id}
              isDraggingColumn={dragColumnId === p.id}
              isRenaming={isRenaming}
              renameValue={renameValue}
              onRenameChange={setRenameValue}
              onRenameCommit={commitRename}
              onRenameCancel={cancelRename}
              onStartRename={() => startRename(p)}
              onDelete={() => removePortfolio(p)}
              onDragStartSite={handleDragStart}
              onDragEndSite={handleDragEnd}
              onDragOver={handleDragOver(p.id)}
              onDragLeave={handleDragLeave(p.id)}
              onDrop={handleDrop(p.id)}
              onColumnDragStart={handleColumnDragStart(p.id)}
              onColumnDragEnd={handleDragEnd}
              onColumnDragOver={handleColumnDragOver(p.id)}
              onColumnDragLeave={handleColumnDragLeave(p.id)}
              onColumnDrop={handleColumnDrop(p.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

function PortfolioColumn({
  columnId, name, sites, hover, isUnassigned, isRenaming, renameValue,
  onRenameChange, onRenameCommit, onRenameCancel, onStartRename, onDelete,
  onDragStartSite, onDragEndSite, onDragOver, onDragLeave, onDrop,
  // Column-reorder props — only supplied for non-Unassigned columns.
  reorderHover, isDraggingColumn,
  onColumnDragStart, onColumnDragEnd, onColumnDragOver, onColumnDragLeave, onColumnDrop,
}) {
  const columnDraggable = !isUnassigned && !isRenaming;
  const columnClasses = [
    "shrink-0 w-64 border transition-colors",
    hover ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-[#27272A] bg-[#0A0A0A]",
    reorderHover ? "ring-2 ring-[#FFCC00]/70 ring-offset-2 ring-offset-[#0A0A0A]" : "",
    isDraggingColumn ? "opacity-40" : "",
  ].join(" ");
  return (
    <div
      data-testid={`portfolio-column-${columnId}`}
      onDragOver={(e) => {
        // Prioritize column-reorder hover on non-Unassigned columns.
        if (onColumnDragOver) onColumnDragOver(e);
        onDragOver(e);
      }}
      onDragLeave={(e) => {
        if (onColumnDragLeave) onColumnDragLeave(e);
        onDragLeave(e);
      }}
      onDrop={(e) => {
        // Try column-drop first — if the source is a column drag, this
        // handler stops propagation and the site drop is skipped.
        if (onColumnDrop) onColumnDrop(e);
        if (!e.defaultPrevented) onDrop(e);
      }}
      className={columnClasses}
    >
      <div
        className={`flex items-center gap-1 px-3 py-2 border-b border-[#27272A] bg-[#111] ${columnDraggable ? "cursor-grab active:cursor-grabbing" : ""}`}
        draggable={columnDraggable}
        onDragStart={columnDraggable ? onColumnDragStart : undefined}
        onDragEnd={columnDraggable ? onColumnDragEnd : undefined}
        data-testid={`portfolio-column-header-${columnId}`}
      >
        {columnDraggable && (
          <GripVertical
            className="size-3.5 text-zinc-600 shrink-0"
            aria-hidden
            title="Drag to reorder this portfolio"
          />
        )}
        {isRenaming ? (
          <>
            <input
              autoFocus
              data-testid={`portfolio-rename-input-${columnId}`}
              className={`${inputClass} flex-1 py-1 text-sm`}
              value={renameValue}
              onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onRenameCommit();
                else if (e.key === "Escape") onRenameCancel();
              }}
            />
            <button data-testid={`portfolio-rename-save-${columnId}`} onClick={onRenameCommit} className="p-1 text-[#22C55E] hover:bg-[#22C55E]/10"><Check className="size-3.5" /></button>
            <button data-testid={`portfolio-rename-cancel-${columnId}`} onClick={onRenameCancel} className="p-1 text-zinc-500 hover:bg-zinc-800"><X className="size-3.5" /></button>
          </>
        ) : (
          <>
            <div className="font-display font-black uppercase tracking-tight text-sm flex-1 truncate" data-testid={`portfolio-column-name-${columnId}`}>
              {name}
            </div>
            <PillBadge tone={isUnassigned && sites.length > 0 ? "amber" : "muted"} data-testid={`portfolio-column-count-${columnId}`}>{sites.length}</PillBadge>
            {!isUnassigned && (
              <>
                <button data-testid={`portfolio-rename-${columnId}`} onClick={onStartRename} title="Rename" className="p-1 text-zinc-500 hover:text-white hover:bg-zinc-800"><Pencil className="size-3" /></button>
                <button data-testid={`portfolio-delete-${columnId}`} onClick={onDelete} title="Delete" className="p-1 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="size-3" /></button>
              </>
            )}
          </>
        )}
      </div>
      <div className="p-2 space-y-2 min-h-[120px] max-h-[60vh] overflow-y-auto">
        {sites.length === 0 && (
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 text-center py-8">
            {isUnassigned ? "No sites need a home" : "Drop sites here"}
          </div>
        )}
        {sites.map((s) => (
          <SiteCard
            key={s.id}
            site={s}
            onDragStart={onDragStartSite(s.id)}
            onDragEnd={onDragEndSite}
          />
        ))}
      </div>
    </div>
  );
}

function SiteCard({ site, onDragStart, onDragEnd }) {
  // Compact address — prefer street when present, fall back to city/state.
  // Keeps the card scannable at a glance without dominating the column.
  const streetLine = site.street_address || site.address || null;
  const cityLine = [site.city, site.state, site.zip_code].filter(Boolean).join(", ");
  const guardsRequired = Number.isFinite(site.guards_required) ? site.guards_required : null;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`portfolio-site-card-${site.id}`}
      className="group cursor-grab active:cursor-grabbing border border-[#27272A] bg-[#141414] px-2 py-2 hover:border-[#3F3F46] transition-colors"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="size-3 text-zinc-600 mt-0.5 shrink-0 opacity-60 group-hover:opacity-100" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-zinc-100 truncate flex items-center gap-1">
            <Building2 className="size-3 text-zinc-500 shrink-0" />
            <span className="truncate" data-testid={`portfolio-site-card-name-${site.id}`}>{site.name}</span>
          </div>
          {streetLine && (
            <div className="text-[11px] text-zinc-400 truncate mt-0.5 pl-4" title={streetLine} data-testid={`portfolio-site-card-street-${site.id}`}>
              {streetLine}
            </div>
          )}
          {cityLine && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate mt-0.5 pl-4" data-testid={`portfolio-site-card-city-${site.id}`}>
              {cityLine}
            </div>
          )}
          {guardsRequired != null && (
            <div className="flex items-center gap-1 mt-1.5 pl-4">
              <PillBadge
                tone={guardsRequired > 1 ? "amber" : "muted"}
                data-testid={`portfolio-site-card-guards-${site.id}`}
              >
                {guardsRequired} guard{guardsRequired === 1 ? "" : "s"} required
              </PillBadge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
